import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import NotificationService from '../services/NotificationService';
import { useAuth } from '../../auth/hooks/useAuth';
import { useSchool } from '../../../contexts/SchoolContext';

/**
 * Main hook for notification operations
 *
 * Features:
 * - Real-time listener for current user's notifications
 * - Mark as read/dismissed
 * - Filter by type, priority, read status
 * - Group notifications by key
 * - Unread count
 */
export const useNotifications = ({
  limit = 100,
  includeRead = true,
  types = [],
  priorities = [],
  autoSubscribe = true,
} = {}) => {
  const { user } = useAuth();
  const { schoolId } = useSchool();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listener
  useEffect(() => {
    if (!autoSubscribe || !user?.uid || !schoolId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build query for user's notifications
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('schoolId', '==', schoolId),
        where('recipientIds', 'array-contains', user.uid),
        orderBy('createdAt', 'desc'),
        firestoreLimit(limit)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const newNotifications = [];
          snapshot.forEach((doc) => {
            const data = doc.data();

            // Filter out dismissed notifications
            if (!data.dismissedBy?.includes(user.uid)) {
              // Filter out expired notifications
              const now = new Date();
              const expiresAt = data.expiresAt?.toDate();
              if (!expiresAt || expiresAt > now) {
                newNotifications.push({ id: doc.id, ...data });
              }
            }
          });

          setNotifications(newNotifications);
          setLoading(false);
        },
        (err) => {
          console.error('Error in notification listener:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      return unsubscribe;
    } catch (err) {
      console.error('Error setting up notification listener:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [user?.uid, schoolId, limit, autoSubscribe]);

  // Filter notifications by type and priority
  const filteredNotifications = useMemo(() => {
    let filtered = [...notifications];

    // Filter by read status
    if (!includeRead) {
      filtered = filtered.filter((n) => !n.readBy?.includes(user?.uid));
    }

    // Filter by types
    if (types.length > 0) {
      filtered = filtered.filter((n) => types.includes(n.type));
    }

    // Filter by priorities
    if (priorities.length > 0) {
      filtered = filtered.filter((n) => priorities.includes(n.priority));
    }

    return filtered;
  }, [notifications, includeRead, types, priorities, user?.uid]);

  // Group notifications by groupKey
  const groupedNotifications = useMemo(() => {
    const grouped = [];
    const groupedItems = new Set();

    filteredNotifications.forEach((notification) => {
      // If it's a group header, add it
      if (notification.isGrouped && notification.groupKey) {
        grouped.push({
          ...notification,
          type: 'group',
        });

        // Mark all items with same groupKey as grouped
        filteredNotifications.forEach((n) => {
          if (n.groupKey === notification.groupKey && !n.isGrouped) {
            groupedItems.add(n.id);
          }
        });
      }
      // If not part of a group, add individually
      else if (!groupedItems.has(notification.id)) {
        grouped.push(notification);
      }
    });

    return grouped;
  }, [filteredNotifications]);

  // Calculate unread count
  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.readBy?.includes(user?.uid)).length;
  }, [notifications, user?.uid]);

  // Mark as read
  const markAsRead = useCallback(
    async (notificationId) => {
      if (!user?.uid) return;

      try {
        await NotificationService.markAsRead(notificationId, user.uid);
      } catch (error) {
        console.error('Error marking notification as read:', error);
        throw error;
      }
    },
    [user?.uid]
  );

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const unreadIds = notifications
        .filter((n) => !n.readBy?.includes(user.uid))
        .map((n) => n.id);

      if (unreadIds.length > 0) {
        await NotificationService.markMultipleAsRead(unreadIds, user.uid);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
      throw error;
    }
  }, [notifications, user?.uid]);

  // Dismiss notification
  const dismiss = useCallback(
    async (notificationId) => {
      if (!user?.uid) return;

      try {
        await NotificationService.dismiss(notificationId, user.uid);
      } catch (error) {
        console.error('Error dismissing notification:', error);
        throw error;
      }
    },
    [user?.uid]
  );

  return {
    notifications: groupedNotifications,
    allNotifications: filteredNotifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    dismiss,
  };
};
